// src/features/procedures/components/ProcedureStepsEditor.jsx
import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Button,
  Alert,
  Stack,
} from '@mui/material';
import {
  Add as AddIcon,
} from '@mui/icons-material';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';

import EditableStepCard from './EditableStepCard';

export default function ProcedureStepsEditor({
  steps = [],
  onStepsChange,
  expandedStepId,
  onExpandedStepIdChange,
  disabled = false,
}) {
  const { t } = useTranslation(['procedures']);

  // Sensors for drag and drop
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Drag and drop handling
  const handleDragEnd = useCallback((event) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = steps.findIndex((item) => item.step_id === active.id);
      const newIndex = steps.findIndex((item) => item.step_id === over.id);

      const reordered = arrayMove(steps, oldIndex, newIndex);

      // Update step_order for all items
      const updatedSteps = reordered.map((item, index) => ({
        ...item,
        step_order: index,
      }));

      onStepsChange(updatedSteps);
    }
  }, [steps, onStepsChange]);

  // Step management
  const handleAddStep = useCallback(() => {
    // Create a default YES_NO step with minimal configuration
    const stepWithOrder = {
      step_id: `temp-${Date.now()}`, // Temporary ID for new steps
      step_order: steps.length,
      step_type: 'YES_NO',
      label: '',
      description: '',
      is_required: true,
      options: {
        yes_label: 'Yes',
        no_label: 'No',
        default_value: 'yes',
      },
    };
    onStepsChange([...steps, stepWithOrder]);
    onExpandedStepIdChange(stepWithOrder.step_id); // Auto-expand newly added step
  }, [steps, onStepsChange, onExpandedStepIdChange]);

  const handleUpdateStep = useCallback((stepId, updatedStep) => {
    const updatedSteps = steps.map(step =>
      step.step_id === stepId ? { ...step, ...updatedStep } : step
    );
    onStepsChange(updatedSteps);
  }, [steps, onStepsChange]);

  const handleDeleteStep = useCallback((stepId) => {
    const filtered = steps.filter(step => step.step_id !== stepId);
    // Reorder remaining steps
    const reordered = filtered.map((step, index) => ({
      ...step,
      step_order: index,
    }));
    onStepsChange(reordered);
  }, [steps, onStepsChange]);

  return (
    <>
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
        <Typography variant="h6">
          {t('procedures:form.steps_section')} ({steps.length})
        </Typography>
        <Button
          variant="contained"
          size="small"
          startIcon={<AddIcon />}
          onClick={handleAddStep}
          disabled={disabled}
        >
          {t('procedures:actions.add_step')}
        </Button>
      </Box>

      {steps.length === 0 ? (
        <Alert severity="info">
          {t('procedures:messages.no_steps')}
        </Alert>
      ) : (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={steps.map(s => s.step_id)}
            strategy={verticalListSortingStrategy}
          >
            <Stack spacing={2}>
              {steps.map((step, index) => (
                <EditableStepCard
                  key={step.step_id}
                  step={step}
                  stepNumber={index + 1}
                  onUpdate={(updatedStep) => handleUpdateStep(step.step_id, updatedStep)}
                  onDelete={() => handleDeleteStep(step.step_id)}
                  disabled={disabled}
                  isExpanded={expandedStepId === step.step_id}
                  onToggleExpand={() => onExpandedStepIdChange(expandedStepId === step.step_id ? null : step.step_id)}
                />
              ))}
            </Stack>
          </SortableContext>
        </DndContext>
      )}
    </>
  );
}
